import {Box} from '@mantine/core';
import type React from 'react';
import type {
  RecruitmentDetailSchedule,
  RecruitmentSchedulesScheme,
} from '../../../../../core/entities/recruitment/schema';
import {cn} from '../../../../../lib/classname';
import {ScheduleButtonNewDesign} from '../../../../recruitment/components/ScheduleButton';
import {formatScheduleTexts} from '../../../usecase/formatScheduleTexts';
import {Deadline} from './Deadline';

type Props = {
  schedules: RecruitmentSchedulesScheme[] | RecruitmentDetailSchedule[];
  withCalendar?: boolean;
  withRegisterButton?: boolean;
  withDeadlineText?: boolean;
  buttonType?: 'text' | 'icon' | 'textWithBorder';
};

const ScheduleText = ({
  title,
  text,
  bold = false,
}: {title: string; text: string; bold?: boolean}) => {
  return (
    <div className="flex pc:text-base text-sm items-center">
      <div className="border-[1px] border-black-2 rounded-full px-8 inline-block mr-8 text-nowrap">
        {title}
      </div>
      <div
        className={cn('inline-block whitespace-normal ', {
          'font-bold': bold,
        })}
      >
        {text}
      </div>
    </div>
  );
};

const EventDateAndPlace = (
  schedule: RecruitmentSchedulesScheme | RecruitmentDetailSchedule,
  withDeadlineText: boolean,
) => {
  const formattedTexts = formatScheduleTexts(schedule);
  return (
    <>
      {formattedTexts.eventDate && (
        <div className="inline-block w-96 whitespace-normal pc:py-4">
          <ScheduleText title="日程" text={formattedTexts.eventDate} />
        </div>
      )}
      {formattedTexts.place && (
        <div className="pc:py-4">
          <ScheduleText title="場所" text={formattedTexts.place} />
        </div>
      )}
      {withDeadlineText && formattedTexts.deadline && (
        <div className="pc:py-4">
          <ScheduleText title="締切" text={formattedTexts.deadline} bold />
        </div>
      )}
    </>
  );
};

export const Schedules: React.FC<Props> = ({
  schedules,
  withCalendar,
  withRegisterButton,
  withDeadlineText = false,
  buttonType = 'text',
}) => {
  const isRegistered = (
    schedule: RecruitmentSchedulesScheme | RecruitmentDetailSchedule,
  ) => {
    return (
      (schedule as RecruitmentSchedulesScheme).isCalendarRegistered ??
      (schedule as RecruitmentDetailSchedule).hasAddCalendar ??
      false
    );
  };
  const registeredCount = (
    schedule: RecruitmentSchedulesScheme | RecruitmentDetailSchedule,
  ) => {
    return (
      (schedule as RecruitmentSchedulesScheme).calendarRegisteredCount ??
      (schedule as RecruitmentDetailSchedule).addCalendarUsers ??
      0
    );
  };
  return (
    <>
      {schedules.map((schedule) => (
        <Box
          key={schedule.id}
          className="flex flex-col bg-background-2 text-black-1 pc:rounded-16 rounded-4 items-center pc:p-16 p-12"
        >
          <div className="flex w-full justify-between gap-8">
            <div className="flex">
              <Box className="grow grid gap-4 w-full">
                <Box className="flex">
                  <div className="font-bold pc:text-md text-sm line-clamp-2">
                    {schedule.name}
                  </div>
                </Box>
                {withCalendar && (
                  <div>{EventDateAndPlace(schedule, withDeadlineText)}</div>
                )}
              </Box>
            </div>
            <div className="flex flex-col justify-start items-end gap-8">
              {withCalendar && <Deadline schedule={schedule} />}
              {withRegisterButton && (
                <ScheduleButtonNewDesign
                  scheduleId={schedule.id}
                  isRegisteredInitial={isRegistered(schedule)}
                  registeredCountInitial={registeredCount(schedule)}
                  buttonStyle={buttonType}
                />
              )}
            </div>
          </div>

          {!withCalendar && (
            <div className="w-full flex flex-col items-start gap-8 py-8">
              {EventDateAndPlace(schedule, withDeadlineText)}
            </div>
          )}
        </Box>
      ))}
    </>
  );
};
